import React from 'react';
import ErrorBoundary, { ErrorFallbackProps } from './ErrorBoundary';

interface ComponentErrorBoundaryProps {
  children: React.ReactNode;
  name?: string;
  fallbackComponent?: React.ComponentType<ErrorFallbackProps>;
  minimal?: boolean;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

const ComponentErrorFallback: React.FC<ErrorFallbackProps & { 
  name?: string; 
  minimal?: boolean 
}> = ({ 
  error, 
  resetError, 
  errorId,
  name = 'Component',
  minimal = false 
}) => {
  if (minimal) {
    return (
      <div style={{
        padding: '0.5rem',
        border: '1px solid var(--color-accent)',
        borderRadius: 'var(--border-radius-sm)',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        color: 'var(--color-accent)',
        fontSize: '0.875rem',
        textAlign: 'center'
      }}>
        <span>⚠️ {name} failed to load</span>
        <button 
          onClick={resetError}
          style={{
            marginLeft: '0.5rem',
            padding: '0.25rem 0.5rem',
            backgroundColor: 'transparent',
            border: '1px solid var(--color-accent)',
            borderRadius: 'var(--border-radius-sm)',
            color: 'var(--color-accent)',
            cursor: 'pointer',
            fontSize: '0.75rem'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  const containerStyle: React.CSSProperties = {
    padding: '1rem',
    border: '2px dashed var(--color-accent)',
    borderRadius: 'var(--border-radius-md)',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    color: '#ffffff',
    textAlign: 'center',
    margin: '0.5rem 0'
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    marginBottom: '0.5rem',
    color: 'var(--color-accent)'
  };

  const messageStyle: React.CSSProperties = {
    fontSize: '0.9rem',
    marginBottom: '1rem',
    opacity: 0.8
  };

  const buttonStyle: React.CSSProperties = {
    padding: '0.5rem 1rem',
    backgroundColor: 'var(--color-accent)',
    color: '#ffffff',
    border: 'none',
    borderRadius: 'var(--border-radius-sm)',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '600',
    transition: 'var(--transition-fast)'
  };

  return (
    <div style={containerStyle}>
      <h3 style={titleStyle}>⚠️ {name} Error</h3>
      <p style={messageStyle}>
        This component encountered an error and couldn't render properly.
      </p>
      <button 
        style={buttonStyle}
        onClick={resetError}
        onMouseOver={(e) => e.currentTarget.style.opacity = '0.8'}
        onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
      >
        Try Again
      </button>
      
      {process.env.NODE_ENV === 'development' && (
        <details style={{ marginTop: '1rem', textAlign: 'left' }}>
          <summary style={{ cursor: 'pointer', fontSize: '0.875rem' }}>
            Error Details
          </summary>
          <pre style={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.1)', 
            padding: '0.5rem', 
            borderRadius: 'var(--border-radius-sm)',
            fontSize: '0.75rem',
            overflow: 'auto',
            maxHeight: '150px'
          }}>
            {error?.message}
            {`\nError ID: ${errorId}`}
          </pre>
        </details>
      )}
    </div>
  );
};

const ComponentErrorBoundary: React.FC<ComponentErrorBoundaryProps> = ({ 
  children, 
  name,
  fallbackComponent,
  minimal = false,
  onError
}) => {
  return (
    <ErrorBoundary
      fallbackComponent={
        fallbackComponent || 
        ((props) => (
          <ComponentErrorFallback {...props} name={name} minimal={minimal} />
        ))
      }
      onError={onError}
      resetOnPropsChange={true}
      isolate={true}
    >
      {children}
    </ErrorBoundary>
  );
};

export default ComponentErrorBoundary;